import md5 from "md5";
import TrieSearch from 'trie-search';
import { Plugin } from 'prosemirror-state';
import { Extension } from '@tiptap/core';
import { formatDistanceToNow, format, parseISO } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { RRule, rrulestr } from 'rrule';

// Locale mapping
const locales = {
  en: enUS,
  fr: fr,
};

const getBrowserLocale = () => {
  if (typeof window === 'undefined') return 'en';

  const browserLang = navigator.language || navigator.userLanguage;

  const langCode = browserLang.split('-')[0];

  return locales[langCode] ? langCode : 'en';
};

const getLocale = (locale) => {
  const localeToUse = locale || getBrowserLocale();
  return locales[localeToUse] || locales.en;
};

let validDomains = [];

export const preloadDomains = () => {
  fetch("/email_provider_domains.txt")
    .then((response) => response.text())
    .then((text) => {
      validDomains = text.split("\n").map((domain) => domain.trim());
    })
    .catch((err) => console.error("Failed to load email domains", err));
};

// preloadDomains()

const validateEmailDomain = (email) => {
  const emailDomain = email.split("@")[1];
  return validDomains.includes(emailDomain);
};

// Validate email format and domain synchronously
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Remove leading/trailing whitespace
  email = email.trim();
  const emailRegex = /^[a-zA-Z0-9][a-zA-Z0-9._-]*@[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;

  return emailRegex.test(email);
};

export function getAPIURL(endpoint = "") {
  if (typeof window !== 'undefined') {
    const frontendDomain = window.location.hostname;
    const protocol = window.location.protocol;
    const firstDot = frontendDomain.indexOf(".");
    const baseURL = frontendDomain.slice(firstDot + 1);
    const isStaging = frontendDomain.startsWith("staging");
    const publicAPIURL = isStaging ? "api.workpeg.com" : process.env.NEXT_PUBLIC_API_URL

    return `${protocol}//${publicAPIURL}/v1/${endpoint}`;
  }

  // Fallback for server-side rendering
  return `https://${process.env.NEXT_PUBLIC_API_URL}/v1/${endpoint}`;
}

export function setTokenExpiry(ttl) {
  if (typeof window !== 'undefined') {
    const expires = new Date();
    expires.setSeconds(expires.getSeconds() + ttl);
    document.cookie = "tokenExpiry=" + expires + "; domain=.workpeg.com; path=/; SameSite=Strict";
  }
}

export function getCachedBusiness() {
  if (typeof window !== 'undefined') {
    return window.localStorage.getItem("cachedBusiness");
  }
  return null;
}

export function setCachedBusiness(business) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem("cachedBusiness", business);
  }
}

export const fetchProtected = async (method, url, payload, headers) => {
  return await fetch(getAPIURL(`${url}`), {
    method: method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(headers || {}),
    },
    body: JSON.stringify(payload),
  });
};

export const fetchUnprotected = async (method, url, payload) => {
  return await fetch(getAPIURL(`${url}`), {
    method: method,
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
};

export const toMonthYear = (dateString, locale = 'en') => {
  const date = new Date(dateString);
  return format(date, 'MMMM, yyyy', { locale: getLocale(locale) });
};

export const toReadableDate = (dateString, locale) => {
  const date = new Date(dateString);
  return format(date, 'PPP', { locale: getLocale(locale) });
};


export const toReadableDateTime = (dateString, locale) => {
  // Return empty string if no date is provided
  if (!dateString) return '';

  // Normalize the date string - if it doesn't have 'Z' or timezone, treat as UTC
  let normalizedDateString = dateString;

  // Handle Date objects
  if (dateString instanceof Date) {
    normalizedDateString = dateString.toISOString();
  } else if (typeof dateString === 'string') {
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('T')) {
      normalizedDateString = dateString + 'Z';
    } else if (dateString.includes('T') && !dateString.endsWith('Z') && !dateString.includes('+')) {
      // Has 'T' but no timezone indicator - treat as UTC
      normalizedDateString = dateString + 'Z';
    }
  }

  const date = new Date(normalizedDateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    console.error('Invalid date:', dateString);
    return '';
  }

  const localeToUse = locale || getBrowserLocale();
  const localeObj = getLocale(locale);

  // Use local time methods to display in user's timezone
  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  // Validate date parts
  if (isNaN(day) || isNaN(monthIndex) || isNaN(year)) {
    console.error('Invalid date parts:', { day, monthIndex, year, dateString });
    return '';
  }

  // Get month name using a temporary date in local time for formatting
  const tempDate = new Date(year, monthIndex, 1);
  const month = format(tempDate, 'MMM', { locale: localeObj });

  // Format time in local timezone
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12; // Convert to 12-hour format
  const time = `${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;

  // Add ordinal suffix for day (only for English)
  const daySuffix = (d) => {
    if (localeToUse === 'en') {
      if (d > 3 && d < 21) return 'th';
      switch (d % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    }
    return '';
  };

  const suffix = daySuffix(day);
  return `${day}${suffix} ${month}, ${year}, ${time}`;
};

// Time distance (already using date-fns, just adding locale support)
export const toTimeDistance = (date, locale) => {
  return formatDistanceToNow(date, {
    addSuffix: true,
    locale: getLocale(locale)
  });
};

// Time of day greeting with locale support
export const timeOfTheDay = (t, locale) => {
  const hour = new Date().getHours();

  // If translation function is provided, use it
  if (t) {
    if (hour < 12) return t('home.goodMorning');
    if (hour < 18) return t('home.goodAfternoon');
    return t('home.goodEvening');
  }

  // Otherwise, provide locale-based defaults
  const localeToUse = locale || getBrowserLocale();
  const greetings = {
    en: {
      morning: 'Good morning',
      afternoon: 'Good afternoon',
      evening: 'Good evening'
    },
    fr: {
      morning: 'Bonjour',
      afternoon: 'Bon après-midi',
      evening: 'Bonsoir'
    }
  };

  const localeGreetings = greetings[localeToUse] || greetings.en;

  if (hour < 12) return localeGreetings.morning;
  if (hour < 18) return localeGreetings.afternoon;
  return localeGreetings.evening;
};

// Get current month and year with locale support
export function getCurrentMonthYear(locale) {
  const currentDate = new Date();
  return format(currentDate, 'MMMM, yyyy', { locale: getLocale(locale) });
}

export const toReadableTime = (dateString) => {
  const date = parseISO(dateString);
  const now = new Date();
  const daysDiff = differenceInDays(now, date);

  if (isToday(date)) {
    return format(date, 'h:mmaaa').toLowerCase(); // e.g., 2:53pm
  } else if (isYesterday(date)) {
    return 'Yesterday';
  } else if (daysDiff < 7) {
    return format(date, 'EEEE'); // e.g., Tuesday
  } else if (daysDiff < 365) {
    return format(date, 'MMM d'); // e.g., Jun 10
  } else {
    return format(date, 'MMM d, yyyy'); // e.g., Jun 10, 2023
  }
};

export const formatTimeZoneLabel = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = new Date();

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'shortOffset',
  });

  const offsetPart = formatter
    .formatToParts(now)
    .find(p => p.type === 'timeZoneName')?.value || 'GMT';

  const normalizedOffset = offsetPart.replace(
    /GMT([+-]\d+)/,
    (_, h) => `GMT${h.padStart(3, '0')}:00`
  );

  const city = timeZone.split('/')[1].replace('_', ' ');

  return `(${normalizedOffset}) ${city}`;
}

export const toReadUnit = (subunit) => {
  return subunit / 100;
};

export const toSubunit = (unit) => {
  return unit * 100;
};

export const commalize = (amount) => {
  // 1000000 => 1,000,000
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const stripCommas = (amount) => {
  return amount.toString().replace(/,/g, "");
};

export const getDefaultAvatar = (name, color) => {
  const firstChar = name[0].toUpperCase();
  color = color || "003664";
  const defaultAvatar = `https://ui-avatars.com/api/${firstChar}/200/${color}/FFF`;
  return defaultAvatar;
};

export const getGravatar = (email, name) => {
  const isBrowser = typeof window !== 'undefined';

  const prefersDarkMode = isBrowser &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  const bgColor = prefersDarkMode ? "1c1d1e" : "d4d4d4";
  const textColor = prefersDarkMode ? "696A6A" : "696a6a";

  const initial = name ? name.charAt(0).toUpperCase() : "?";

  const defaultAvatar = encodeURIComponent(
    `https://ui-avatars.com/api/${initial}/200/${bgColor}/${textColor}`
  );

  // Generate Gravatar URL
  const hash = md5(email.trim().toLowerCase());
  return `https://www.gravatar.com/avatar/${hash}?d=${defaultAvatar}`;
};

export const base7ToBase10 = (number) => {
  return parseInt(number, 7);
};

export const getCommandKey = (mac, wind) => {
  const isAppleDevice =
    /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    navigator.platform.toLowerCase().includes('mac');
  return isAppleDevice ? mac : wind;
};

export const generateUniqueId = (existingIds, length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let id;
  do {
    id = Array.from({ length }, () => characters.charAt(Math.floor(Math.random() * characters.length))).join('');
  } while (existingIds.includes(id));
  return id;
};
const commonDomains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'protonmail.com',
  'mail.com',
  'zoho.com',
  'microsoft.com',
  'live.com',
  'me.com',
  'ng.workpeg.com'
];

const trie = new TrieSearch('domain');
const domainObjects = commonDomains.map(domain => ({ domain }));
trie.addAll(domainObjects);

export const getSuggestions = (currentEmail, referenceEmail) => {
  if (!currentEmail.includes('@')) return [];

  const referenceDomain = referenceEmail?.split('@')[1];
  const [, partialDomain] = currentEmail?.split('@');

  if (!partialDomain) {
    const suggestions = [`@${referenceDomain}`];
    return suggestions.concat(commonDomains.filter(domain => domain !== referenceDomain).map(d => `@${d}`));
  }

  let suggestions = trie.get(partialDomain).map(result => `@${result.domain}`);

  if (referenceDomain.startsWith(partialDomain)) {
    suggestions = suggestions.filter(s => s !== `@${referenceDomain}`);
    suggestions.unshift(`@${referenceDomain}`);
  }

  return suggestions;
};


export const NearestClick = Extension.create({
  name: 'nearestClick',

  addOptions() {
    return {
      selectors: ['[data-row-id]'],
      onNearestClick: () => { },
      ignore: (event) => {
        // Ignore clicks on these elements
        return (
          event.target.closest('.ProseMirror') ||
          event.target.closest('.ignore-click') ||
          event.target.closest('.secondary-dropdown-container') ||
          event.target.closest('[data-table-control]') ||
          event.target.closest('.table-toolbar') ||
          event.target.closest('.table-cell-menu-dropdown')
        );
      }
    }
  },

  onCreate() {
    const handleGlobalClick = (event) => {
      // Check if clicking on any UI elements we should ignore
      if (this.options.ignore(event)) {
        return;
      }

      // Check if we have an active toolbar
      const hasActiveToolbar = document.querySelector('.editor-toolbar-active') ||
        (typeof activeToolbarState !== 'undefined' && activeToolbarState.isActive);

      // If clicking outside when toolbar is active, let the click propagate normally
      // This allows the click to dismiss the toolbar
      if (hasActiveToolbar) {
        return;
      }

      // If user is selecting text, don't interfere
      const selection = window.getSelection();
      if (selection.toString().trim() !== '') {
        return;
      }

      // Now handle nearest click for regular document interaction
      const { threshold, onNearestClick } = this.options
      const rows = Array.from(document.querySelectorAll('[data-row-id]'))
      if (rows.length === 0) return

      const clickX = event.clientX
      const clickY = event.clientY

      const distances = rows.map(row => {
        const rect = row.getBoundingClientRect()
        const closestX = Math.max(rect.left, Math.min(clickX, rect.right))
        const closestY = Math.max(rect.top, Math.min(clickY, rect.bottom))

        const distance = Math.sqrt(
          Math.pow(clickX - closestX, 2) +
          Math.pow(clickY - closestY, 2)
        )

        return {
          element: row,
          distance,
          rowId: row.getAttribute('data-row-id'),
          coords: {
            click: { x: clickX, y: clickY },
            closest: { x: closestX, y: closestY },
            rect: {
              top: rect.top,
              right: rect.right,
              bottom: rect.bottom,
              left: rect.left
            }
          }
        }
      })

      const nearest = distances
        .filter(({ distance }) => distance <= threshold)
        .sort((a, b) => a.distance - b.distance)[0]

      if (nearest) {
        const editorElement = nearest.element.querySelector('.ProseMirror')
        if (editorElement) {
          editorElement.focus()
          onNearestClick({
            element: nearest.element,
            rowId: nearest.rowId,
            distance: nearest.distance,
            event,
            coords: nearest.coords
          })
        }
      }
    }

    // Use regular event listener to allow normal event propagation
    window.addEventListener('click', handleGlobalClick)
    this.handleGlobalClick = handleGlobalClick
  },

  onDestroy() {
    if (this.handleGlobalClick) {
      window.removeEventListener('click', this.handleGlobalClick)
    }
  },

  addProseMirrorPlugins() {
    return [new Plugin({})]
  }
})

export const generateUniqueFileName = (file, files) => {
  let fileName = file?.alias || file?.name;

  if (!files || files.length === 0) {
    return fileName;
  }

  const fileNames = files.map(f => f.alias);
  let counter = 1;

  const lastDotIndex = fileName?.lastIndexOf('.');
  let nameWithoutExt = fileName;
  let extension = '';

  if (lastDotIndex !== -1) {
    nameWithoutExt = fileName?.substring(0, lastDotIndex);
    extension = fileName?.substring(lastDotIndex);
  }

  while (fileNames.includes(fileName)) {
    fileName = `${nameWithoutExt}(${counter})${extension}`;
    counter++;
  }

  return fileName;
};

export const fileTypeFinder = (file) => {
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'tif', 'ico', 'heic', 'heif'];
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', '3gp', 'mpeg', 'mpg'];
  const audioExtensions = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma', 'aiff', 'alac'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'txt', 'rtf', 'odt', 'odp', 'xml', 'html', 'htm', 'md', 'pages', 'numbers', 'keynote', 'epub', 'mobi', 'azw', 'djvu', 'tex', 'wpd', 'json', 'yaml', 'yml', 'sql', 'log'];
  const spreadsheetExtensions = ['xls', 'xlsx', 'csv', 'numbers', 'ods', 'xlsm', 'xlsb', 'xltx', 'xltm', 'xlr'];
  const zipExtensions = ['zip', 'tar', 'gz', 'bz2', 'xz', '7z', 'rar', 'iso', 'dmg'];
  // const presentationExtensions = ['ppt', 'pptx', 'key', 'odp', 'pps', 'ppsx', 'ppsm', 'pptm', 'pot', 'potx'];
  // const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'iso', 'dmg', 'tgz'];
  // const codeExtensions = ['js', 'py', 'java', 'c', 'cpp', 'cs', 'php', 'rb', 'go', 'ts', 'html', 'css', 'sql', 'sh', 'swift'];
  const extension = file.extension?.toLowerCase() || '';

  if (imageExtensions.includes(extension)) {
    return "image";
  } else if (videoExtensions.includes(extension)) {
    return "video";
  } else if (audioExtensions.includes(extension)) {
    return "audio";
  } else if (documentExtensions.includes(extension)) {
    return "document";
  } else if (spreadsheetExtensions.includes(extension)) {
    return "spreadsheet";
  } else if (file.is_dir) {
    return "folder";
  } else if (zipExtensions.includes(extension)) {
    return "zip";
  } else {
    return "file";
  }
}

export const isValidPhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');

  // Check if it's empty after cleaning
  if (cleanPhone.length === 0) {
    return false;
  }

  // Phone number should be between 7 and 15 digits (international standard)
  if (cleanPhone.length < 7 || cleanPhone.length > 15) {
    return false;
  }

  return true;
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, '');

  // Return the cleaned number
  return cleanPhone;
};

export const getDisplayName = (user, part) => {
  if (!user) return '';

  // Normalize user object structure if it has nested user property
  if (user.user) {
    user = {
      firstName: user.user.first_name,
      lastName: user.user.last_name,
      email: user.user.email || user.work_email,
      alias: user.alias,
      work_email: user.work_email,
    }
  }

  if (part === 'first') {
    return user.firstName || user.alias || user.email || user.work_email || '';
  }

  if (part === 'last') {
    return user.lastName || '';
  }

  if (user.firstName?.trim() && user.lastName?.trim()) {
    return `${user.firstName} ${user.lastName}`.trim();
  }
  if (user.alias?.trim()) {
    return user.alias;
  }
  return user.email || user.work_email || '';
};

export const renderColumnIcon = (column) => {
  // Check metadata.type first, then fall back to column.type
  const columnType = column.metadata?.type || column.type;

  switch (columnType) {
    case 'text':
      return 'list-minus';
    case 'number':
      return 'hash';
    case 'deadline':
    case 'datetime':
      return 'timer';
    case 'teammate':
      return 'user-round';
    case 'mutable_select':
      return 'square';
    case 'currency':
      return 'banknote';
    case 'many_to_many':
      return 'list-collapse';
    case 'url':
      return 'link-2';
    case 'country':
      return 'map-pin';
    case 'phone_no':
      return 'phone';
    case 'file':
      return 'file';
    case 'recurrence':
      return 'repeat';
    case 'markdown':
      return 'square-code';
    default:
      return 'table';
  }
}

export const renderRecurrenceValue = (recurrenceString, startDate, endDate) => {
  if (!recurrenceString) return '';

  try {
    const rule = rrulestr(recurrenceString);
    const opts = rule.options;        // for bymonthday, freq, interval, count, until
    const origOpts = rule.origOptions; // for byweekday (preserves nth position)

    const formatTime = (hour, minute = 0) => {
      if (typeof hour !== 'number' || isNaN(hour) || hour < 0 || hour > 23) return null;
      if (typeof minute !== 'number' || isNaN(minute)) minute = 0;
      const period = hour >= 12 ? 'PM' : 'AM';
      const h = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${h}:${minute.toString().padStart(2, '0')} ${period}`;
    };

    const parseDate = (date) => {
      if (!date) return null;
      if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDate = (date) => {
      const d = parseDate(date);
      if (!d) return null;
      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    };

    const formatDateTime = (date) => {
      const d = parseDate(date);
      if (!d) return null;
      return d.toLocaleString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    // Parse dates
    const start = parseDate(startDate);
    const end = parseDate(endDate);

    // Helper: is start date today?
    const isToday = (date) => {
      if (!date) return false;
      const now = new Date();
      return (
        date.getFullYear() === now.getFullYear() &&
        date.getMonth() === now.getMonth() &&
        date.getDate() === now.getDate()
      );
    };

    // Handle single occurrence (COUNT=1)
    if (opts.count === 1) {
      let description = 'Once';
      if (start) {
        description += ` on ${formatDateTime(start)}`;
      } else if (opts.byhour?.length) {
        const times = opts.byhour
          .map(h => formatTime(h, opts.byminute?.[0]))
          .filter(Boolean)
          .join(' and ');
        if (times) description += ` at ${times}`;
      }
      return description;
    }

    const freqMap = {
      [RRule.DAILY]: 'Daily',
      [RRule.WEEKLY]: 'Weekly',
      [RRule.MONTHLY]: 'Monthly',
      [RRule.YEARLY]: 'Yearly',
    };

    let description = freqMap[opts.freq] || 'Repeats';

    // Add interval if > 1
    if (opts.interval > 1) {
      const intervalMap = {
        [RRule.DAILY]: 'days',
        [RRule.WEEKLY]: 'weeks',
        [RRule.MONTHLY]: 'months',
        [RRule.YEARLY]: 'years',
      };
      description = `Every ${opts.interval} ${intervalMap[opts.freq] || 'times'}`;
    }

    // Add day of week for weekly recurrence
    if (opts.freq === RRule.WEEKLY && origOpts.byweekday?.length) {
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const days = origOpts.byweekday
        .map(day => {
          const dayNum = typeof day === 'number' ? day : day.weekday;
          return dayNames[dayNum];
        })
        .filter(Boolean)
        .join(', ');
      if (days) {
        const weekdayNumbers = origOpts.byweekday.map(d => typeof d === 'number' ? d : d.weekday).sort();
        const isWeekdays = JSON.stringify(weekdayNumbers) === JSON.stringify([0, 1, 2, 3, 4]);
        if (isWeekdays) {
          description += ` on weekdays`;
        } else {
          description += ` on ${days}`;
        }
      }
    }

    // Add day of month for monthly recurrence
    if (opts.freq === RRule.MONTHLY && opts.bymonthday?.length) {
      const day = opts.bymonthday[0];
      const suffix = (d) => {
        if ([11, 12, 13].includes(d)) return 'th';
        return ['th', 'st', 'nd', 'rd'][d % 10] || 'th';
      };
      // -1 means last day in rrule
      if (day === -1) {
        description += ` on the last day of the month`;
      } else {
        description += ` on the ${day}${suffix(day)} day of the month`;
      }
    }

    // Add nth weekday of month for monthly recurrence (e.g. BYDAY=1WE = 1st Wednesday)
    if (opts.freq === RRule.MONTHLY && origOpts.byweekday?.length) {
      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const ordinals = ['', '1st', '2nd', '3rd', '4th', '5th'];
      const negativeOrdinals = { '-1': 'last', '-2': 'second to last' };

      const parts = origOpts.byweekday
        .map(day => {
          const weekday = typeof day === 'number' ? day : day.weekday;
          const n = typeof day === 'object' ? day.n : null;
          const dayName = dayNames[weekday];

          if (n && n > 0) return `the ${ordinals[n]} ${dayName}`;
          if (n && n < 0) return `the ${negativeOrdinals[String(n)] || Math.abs(n) + 'th to last'} ${dayName}`;
          return dayName;
        })
        .filter(Boolean)
        .join(', ');

      if (parts) description += ` on ${parts}`;
    }

    // Add times (from RRULE or from start/end dates)
    // Use origOpts.byhour so we only show times explicitly set in the rule,
    // not the default [0] that RRule injects automatically.
    if (origOpts.byhour?.length) {
      const minutes = origOpts.byminute?.length ? origOpts.byminute : [0];
      const times = origOpts.byhour
        .map((h, i) => formatTime(h, minutes[i] ?? minutes[0]))
        .filter(Boolean)
        .join(' and ');
      if (times) description += ` at ${times}`;
    } else if (start && end) {
      const startH = start.getHours(), startM = start.getMinutes();
      const endH = end.getHours(), endM = end.getMinutes();
      // Only show times if they were explicitly set (not both midnight/zero)
      const startHasTime = startH !== 0 || startM !== 0;
      const endHasTime = endH !== 0 || endM !== 0;
      if (startHasTime || endHasTime) {
        const startTime = formatTime(startH, startM);
        const endTime = endHasTime ? formatTime(endH, endM) : null;
        if (startTime && endTime) {
          description += ` from ${startTime} to ${endTime}`;
        } else if (startTime) {
          description += ` at ${startTime}`;
        }
      }
    } else if (start) {
      const startH = start.getHours(), startM = start.getMinutes();
      // Only show time if it was explicitly set (not midnight/zero)
      if (startH !== 0 || startM !== 0) {
        const startTime = formatTime(startH, startM);
        if (startTime) description += ` at ${startTime}`;
      }
    }

    // Only add "starting ..." if start is set, not today, and not a single occurrence
    if (start && !isToday(start) && opts.count !== 1) {
      const formattedStart = formatDate(start);
      if (formattedStart) description += `, starting ${formattedStart}`;
    }

    // Add end date
    if (opts.until) {
      const formattedUntil = formatDate(opts.until);
      if (formattedUntil) description += ` until ${formattedUntil}`;
    } else if (end && !opts.count) {
      const formattedEnd = formatDate(end);
      if (formattedEnd) description += ` until ${formattedEnd}`;
    } else if (opts.count && opts.count > 1) {
      description += ` for ${opts.count} occurrences`;
    }

    return description;
  } catch (error) {
    console.error('Error parsing recurrence:', error);
    return '';
  }
};